/**
 * WL-K5b — Client helpers for the Kanzlei bulk-onboarding flow (CSV → Job → Status).
 *
 * Two concerns:
 *  1. A PURE, client-side CSV parser + Zod-shaped validation for the pre-submit
 *     PREVIEW (invalid rows, file-internal duplicates via beidseitige E-Mail-
 *     Normalisierung, over-limit). This is a convenience only — the server ALWAYS
 *     re-validates (`createBulkInviteJob`), so a tampered client parse cannot widen
 *     anything (architecture-loop-b.md §4.3 „Client-Parse untrusted").
 *  2. The `createBulkInviteJob` callable binding. The live status table reads the
 *     `bulk_invite_jobs/{jobId}` doc + its `rows` subcollection DIRECTLY via the
 *     Firestore SDK (gated by the row-level `advisorUid` rule) — see BulkInvitePanel.
 *
 * There is NO FE env flag here (like {@link ./clientInviteApi}): the whole channel
 * is gated server-side behind Loop-B-Beta + operator vetting; the UI visibility is
 * gated with `canUseClientInvite(...)` (M-GA-4; `GA_FLAG || isLoopBBeta`) at the
 * component level.
 *
 * @module api/bulkInviteApi
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';

/** Hard row limit per job — mirrors the server `MAX_BULK_ROWS` (SM-9). */
export const MAX_BULK_ROWS = 1000;

/** E-mail format guard — mirrors the server `EMAIL_FORMAT_RE`. */
const EMAIL_FORMAT_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** One parsed CSV row, annotated for the preview table. */
export interface ParsedBulkRow {
  /** 1-based source line number (for error display). */
  lineNumber: number;
  /** Raw e-mail as read from the CSV. */
  email: string;
  /** trim + lowercase (SM-4 beidseitige Normalisierung). */
  emailNorm: string;
  clientName: string | null;
  mandantenNr: string | null;
  /** false = syntactically invalid e-mail. */
  valid: boolean;
  /** true = a later occurrence of an emailNorm already seen above. */
  duplicate: boolean;
}

/** Result of {@link parseBulkCsv}. */
export interface BulkCsvParseResult {
  rows: ParsedBulkRow[];
  validUniqueCount: number;
  invalidCount: number;
  duplicateCount: number;
  /** true when the unique-valid count exceeds {@link MAX_BULK_ROWS}. */
  overLimit: boolean;
}

/** The clean row shape submitted to the server. */
export interface BulkInviteSubmitRow {
  email: string;
  clientName?: string | null;
  mandantenNr?: string | null;
}

/** Server response from `createBulkInviteJob`. */
export interface CreateBulkInviteJobResult {
  jobId: string;
  totalRows: number;
  duplicatesDropped: number;
  invalidDropped: number;
}

/**
 * Splits ONE CSV line into fields, honouring double-quoted fields (which may embed
 * the delimiter and doubled `""` quotes). Minimal RFC-4180-ish parser — enough for
 * e-mail / name / number columns.
 */
function splitCsvLine(line: string, delimiter: string): string[] {
  const fields: string[] = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      continue;
    }
    if (ch === delimiter) {
      fields.push(field);
      field = '';
      continue;
    }
    field += ch;
  }
  fields.push(field);
  return fields.map((f) => f.trim());
}

/** Picks the delimiter (`;` for German Excel exports, else `,`) from the header. */
function detectDelimiter(headerLine: string): string {
  return headerLine.includes(';') && !headerLine.includes(',') ? ';'
    : headerLine.split(';').length > headerLine.split(',').length ? ';' : ',';
}

/** Normalises an e-mail beidseitig (trim + lowercase) — mirrors the server. */
function normalizeEmail(raw: string): string {
  return typeof raw === 'string' ? raw.trim().toLowerCase() : '';
}

/**
 * Resolves the column index for each field from a header row. Recognises common
 * German/English header names; falls back to positional (0=email, 1=name, 2=nr)
 * when no recognisable header is present.
 */
function resolveColumns(header: string[]): { email: number; name: number; nr: number; hasHeader: boolean } {
  const lower = header.map((h) => h.toLowerCase());
  const find = (names: string[]): number => lower.findIndex((h) => names.includes(h));
  const email = find(['email', 'e-mail', 'e-mail-adresse', 'mail']);
  const name = find(['name', 'clientname', 'mandant', 'mandantenname', 'firma', 'company']);
  const nr = find(['mandantennr', 'mandanten-nr', 'mandantennummer', 'datev', 'datev-mandanten-nr', 'nr']);
  const hasHeader = email >= 0;
  return {
    email: email >= 0 ? email : 0,
    name: name >= 0 ? name : 1,
    nr: nr >= 0 ? nr : 2,
    hasHeader,
  };
}

/**
 * Parses raw CSV text into annotated preview rows. Skips fully-empty lines. The
 * first line is treated as a header ONLY when it contains a recognisable e-mail
 * column name; otherwise it is parsed as data (positional columns).
 *
 * @param text the raw CSV file content.
 * @returns {@link BulkCsvParseResult} with per-row validity + duplicate flags.
 */
export function parseBulkCsv(text: string): BulkCsvParseResult {
  const rawLines = text.split(/\r\n|\r|\n/).filter((l) => l.trim() !== '');
  if (rawLines.length === 0) {
    return { rows: [], validUniqueCount: 0, invalidCount: 0, duplicateCount: 0, overLimit: false };
  }
  const delimiter = detectDelimiter(rawLines[0]);
  const header = splitCsvLine(rawLines[0], delimiter);
  const cols = resolveColumns(header);
  const dataLines = cols.hasHeader ? rawLines.slice(1) : rawLines;
  const lineOffset = cols.hasHeader ? 2 : 1; // 1-based line number of the first data row

  const seen = new Set<string>();
  const rows: ParsedBulkRow[] = [];
  let validUniqueCount = 0;
  let invalidCount = 0;
  let duplicateCount = 0;

  dataLines.forEach((line, idx) => {
    const fields = splitCsvLine(line, delimiter);
    const email = (fields[cols.email] ?? '').trim();
    const emailNorm = normalizeEmail(email);
    const clientNameRaw = (fields[cols.name] ?? '').trim();
    const mandantenNrRaw = (fields[cols.nr] ?? '').trim();
    const valid = !!emailNorm && EMAIL_FORMAT_RE.test(emailNorm);
    const duplicate = valid && seen.has(emailNorm);

    if (valid && !duplicate) {
      seen.add(emailNorm);
      validUniqueCount++;
    } else if (!valid) {
      invalidCount++;
    } else if (duplicate) {
      duplicateCount++;
    }

    rows.push({
      lineNumber: idx + lineOffset,
      email,
      emailNorm,
      clientName: clientNameRaw || null,
      mandantenNr: mandantenNrRaw || null,
      valid,
      duplicate,
    });
  });

  return {
    rows,
    validUniqueCount,
    invalidCount,
    duplicateCount,
    overLimit: validUniqueCount > MAX_BULK_ROWS,
  };
}

/** Extracts the clean, unique, valid submit rows from a parse result. */
export function toSubmitRows(parse: BulkCsvParseResult): BulkInviteSubmitRow[] {
  return parse.rows
    .filter((r) => r.valid && !r.duplicate)
    .map((r) => ({
      email: r.emailNorm,
      clientName: r.clientName,
      mandantenNr: r.mandantenNr,
    }));
}

/**
 * Creates a bulk-onboarding job on the server. The server re-validates every row,
 * enforces the ≤1.000 / vetting / AVV / single-active-job guards and returns the
 * job id plus the counts of dropped rows.
 *
 * @param rows the cleaned submit rows (server re-validates regardless).
 * @param advisorConfirmed the mandatory Kanzlei confirmation checkbox value.
 * @param sourceFilename optional original file name (stored on the job for display).
 */
export async function createBulkInviteJob(
  rows: BulkInviteSubmitRow[],
  advisorConfirmed: boolean,
  sourceFilename?: string | null,
): Promise<CreateBulkInviteJobResult> {
  const callable = httpsCallable<
    { rows: BulkInviteSubmitRow[]; advisorConfirmed: boolean; sourceFilename?: string | null },
    CreateBulkInviteJobResult
  >(functions, 'createBulkInviteJob');
  try {
    const res = await callable({ rows, advisorConfirmed, sourceFilename });
    return res.data;
  } catch (err) {
    throw normalizeCallableError(err);
  }
}

/**
 * Maps a Firebase callable error to a plain Error, preserving the server message and
 * exposing the coarse function error code (e.g. `failed-precondition`).
 */
function normalizeCallableError(err: unknown): Error & { code?: string } {
  const anyErr = err as { code?: string; message?: string };
  const message =
    typeof anyErr?.message === 'string' && anyErr.message.length > 0
      ? anyErr.message
      : 'Request failed.';
  const out = new Error(message) as Error & { code?: string };
  if (typeof anyErr?.code === 'string') {
    out.code = anyErr.code.replace(/^functions\//, '');
  }
  return out;
}
